import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Phone, ArrowRight, Scale, Shield, Users, Heart, Award, CheckCircle } from 'lucide-react';
import { Headshot } from '../components/Headshot';

const Home = () => {
  const phoneNumber = '336-354-1040';
  const phoneLink = 'tel:+13363541040';
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // NC and SC city photos for hero slideshow
  const cityscapeImages = [
    'https://customer-assets.emergentagent.com/job_rizzi-practice/artifacts/jf4jd843_ashville.jpg',
    'https://customer-assets.emergentagent.com/job_rizzi-practice/artifacts/lgj527hv_Charlotte-city-skyline.webp',
    'https://customer-assets.emergentagent.com/job_rizzi-practice/artifacts/v3ncjwe8_charlestone.jpg',
    'https://customer-assets.emergentagent.com/job_rizzi-practice/artifacts/jznjj6m8_columbia.webp',
    'https://customer-assets.emergentagent.com/job_rizzi-practice/artifacts/1izwn1w4_greensbourough.jpg',
  ];

  useEffect(() => {
    // Faster rotation: 5.5 seconds per image
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % cityscapeImages.length);
    }, 5500);

    return () => clearInterval(interval);
  }, [cityscapeImages.length]);

  const practiceAreas = [
    {
      title: 'Auto Accidents',
      description: 'Comprehensive representation for accident victims.',
      icon: <Scale className="h-7 w-7" />,
      link: '/practice-areas/auto-accidents',
    },
    {
      title: 'Catastrophic Injury Cases',
      description: 'Dedicated advocacy for severe injuries.',
      icon: <Shield className="h-7 w-7" />,
      link: '/practice-areas/catastrophic-injury',
    },
    {
      title: 'Premises Liability Cases',
      description: 'Holding property owners accountable.',
      icon: <CheckCircle className="h-7 w-7" />,
      link: '/practice-areas/premises-liability',
    },
    {
      title: 'Trucking Accidents',
      description: 'Expert handling of commercial vehicle cases.',
      icon: <Scale className="h-7 w-7" />,
      link: '/practice-areas/trucking-accidents',
    },
    {
      title: 'Workers\' Compensation',
      description: 'Protecting injured workers in North Carolina.',
      icon: <Users className="h-7 w-7" />,
      link: '/practice-areas/workers-compensation',
    },
    {
      title: 'Wrongful Death',
      description: 'Compassionate guidance for families.',
      icon: <Heart className="h-7 w-7" />,
      link: '/practice-areas/wrongful-death',
    },
  ];

  return (
    <div data-testid="home-page" className="font-body">
      {/* Hero Section */}
      <section
        className="relative flex items-center justify-center overflow-hidden"
        style={{ minHeight: '60vh' }}
        data-testid="hero-section"
      >
        {/* Background Images with Crossfade */}
        <div className="absolute inset-0">
          {cityscapeImages.map((image, index) => (
            <div
              key={index}
              className={`hero-slide ${currentImageIndex === index ? 'active' : ''}`}
              style={{
                backgroundImage: `url(${image})`,
              }}
            />
          ))}
        </div>

        {/* Gradient Overlay - stronger for readability */}
        <div
          className="absolute inset-0 z-10"
          style={{
            background: 'linear-gradient(180deg, rgba(10,20,60,0.50) 0%, rgba(10,20,60,0.75) 100%)',
          }}
        />

        {/* Hero Content */}
        <div className="relative z-20 w-full max-w-[1120px] mx-auto px-4 md:px-8 text-center py-12 md:py-0">
          {/* Firm Name */}
          <div className="mb-4 md:mb-6">
            <p className="text-[12px] md:text-[16px] tracking-[0.2em] uppercase font-semibold text-white/90 mb-3" style={{ textShadow: '0 2px 8px rgba(0,0,0,0.4)' }}>
              Rizzi Law Group
            </p>
            <div className="w-12 md:w-16 h-[2px] bg-white/70 mx-auto"></div>
          </div>
          
          <h1 className="text-[28px] sm:text-[36px] md:text-[60px] leading-[1.15] font-heading font-semibold text-white mb-5 md:mb-8 tracking-tight" style={{ textShadow: '0 2px 12px rgba(0,0,0,0.3)' }}>
            North Carolina and South Carolina injury attorneys.
          </h1>
          <p className="text-[15px] md:text-[18px] text-white/95 mb-8 md:mb-12 leading-relaxed max-w-[720px] mx-auto font-light" style={{ textShadow: '0 1px 8px rgba(0,0,0,0.3)' }}>
            When you've been injured, you need advocates who prepare thoroughly and communicate clearly.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center">
            <Link
              to="/contact"
              className="w-full sm:w-auto bg-white text-primary px-8 sm:px-10 py-3.5 sm:py-4 flex items-center justify-center font-semibold hover:bg-slate-50 transition-all duration-300 text-[15px] rounded-xl"
              data-testid="hero-consultation-cta"
            >
              Request a Free Consultation
            </Link>
            <a
              href={phoneLink}
              className="w-full sm:w-auto border-2 border-white text-white px-8 sm:px-10 py-3.5 sm:py-4 flex items-center justify-center font-semibold hover:bg-white/10 transition-all duration-300 text-[15px] rounded-xl"
              data-testid="hero-call-cta"
            >
              Call {phoneNumber}
            </a>
          </div>
        </div>
      </section>

      {/* Trust & Credibility Section */}
      <section className="py-10 md:py-[52px] bg-white" data-testid="trust-credentials-section">
        <div className="w-full max-w-[1120px] mx-auto px-4 md:px-8">
          <div className="text-center">
            <h2 className="text-[24px] md:text-[28px] font-heading font-semibold text-slate-900 mb-2 tracking-tight">
              Trusted Legal Credentials
            </h2>
            <p className="text-[14px] text-slate-600 mb-8">
              Licensed and recognized across the Southeast.
            </p>

            {/* Logos Grid */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-6 mb-6 max-w-[1100px] mx-auto">
              {/* Million Dollar Advocates Forum */}
              <div className="flex items-center justify-center overflow-hidden md:overflow-visible"
                style={{ width: '100%', height: '90px' }}
              >
                <img
                  src="https://customer-assets.emergentagent.com/job_46114de2-d079-4505-8e4e-c293a92431a3/artifacts/cd9y10nf_DAS_-_Million_Dollar_Advocates_Forum_LOGO.png"
                  alt="Million Dollar Advocates Forum"
                  className="max-w-full md:max-w-[280px]"
                  style={{ maxHeight: '80px', objectFit: 'contain' }}
                />
              </div>

              {/* South Carolina Bar */}
              <div className="flex items-center justify-center overflow-hidden"
                style={{ width: '100%', height: '90px' }}
              >
                <img
                  src="https://customer-assets.emergentagent.com/job_rizzi-practice/artifacts/xvlaftna_SC%20Bar.svg"
                  alt="South Carolina Bar"
                  className="max-w-full md:max-w-[180px]"
                  style={{ maxHeight: '54px', objectFit: 'contain' }}
                />
              </div>

              {/* North Carolina State Bar */}
              <div className="flex items-center justify-center overflow-hidden"
                style={{ width: '100%', height: '90px' }}
              >
                <img
                  src="https://customer-assets.emergentagent.com/job_rizzi-practice/artifacts/5t1sp0mc_nc%20bar%20association.webp"
                  alt="North Carolina State Bar"
                  className="max-w-full md:max-w-[180px]"
                  style={{ maxHeight: '54px', objectFit: 'contain' }}
                />
              </div>

              {/* Tennessee Bar */}
              <div className="flex items-center justify-center overflow-hidden"
                style={{ width: '100%', height: '90px' }}
              >
                <img
                  src="https://customer-assets.emergentagent.com/job_rizzi-practice/artifacts/kl645xxp_tenesse%20state%20bar%20association.jpg"
                  alt="Tennessee Bar"
                  className="max-w-full md:max-w-[140px]"
                  style={{ maxHeight: '50px', objectFit: 'contain' }}
                />
              </div>

              {/* Award Badge */}
              <div className="flex items-center justify-center col-span-2 md:col-span-1"
                style={{ width: '100%', height: '110px' }}
              >
                <img
                  src="https://customer-assets.emergentagent.com/job_af12790b-1809-4c45-9b34-f5a55e7733f5/artifacts/42be2t01_image.png"
                  alt="Award Badge"
                  className="max-w-[130px] md:max-w-[130px]"
                  style={{ maxHeight: '110px', objectFit: 'contain' }}
                />
              </div>
            </div>

            {/* Micro Text */}
            <p className="text-[12px] text-slate-500 tracking-wide">
              Licensed Attorneys | Recognized Legal Excellence
            </p>
          </div>
        </div>
      </section>

      {/* Why Clients Choose Our Firm */}
      <section className="py-12 md:py-[72px] bg-slate-50" data-testid="why-choose-firm-section">
        <div className="w-full max-w-[1120px] mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 md:grid-cols-[1.2fr_1fr] gap-6 md:gap-8 lg:gap-12 items-center">
            {/* Left Column - Text Content */}
            <div className="order-1">
              <h2 className="text-[26px] md:text-[34px] leading-[1.2] font-heading font-semibold text-slate-900 mb-5 md:mb-6 tracking-tight">
                Why Clients Choose Our Firm
              </h2>
              
              <div className="space-y-4 md:space-y-5 mb-6 md:mb-8">
                <p className="text-[15px] md:text-[16px] text-slate-700 leading-relaxed">
                  When you are injured because of someone else's negligence, the lawyer you choose matters. Our firm focuses on representing individuals and families who have been seriously injured in accidents. We provide clear guidance, direct communication, and strong advocacy to help clients recover the compensation they deserve.
                </p>
                
                <p className="text-[15px] md:text-[16px] text-slate-700 leading-relaxed">
                  Our attorneys are licensed in North Carolina, South Carolina, and Tennessee, allowing us to represent clients throughout the Southeast. Every case receives careful preparation and personal attention from an experienced legal team committed to protecting your rights and pursuing the strongest possible outcome.
                </p>
              </div>

              {/* Credibility Bullets */}
              <ul className="space-y-3 mb-6">
                <li className="flex items-center gap-3 text-[15px] md:text-[16px] text-slate-700">
                  <span className="text-primary font-bold leading-none">•</span>
                  <span>Direct access to your attorney</span>
                </li>
                <li className="flex items-center gap-3 text-[15px] md:text-[16px] text-slate-700">
                  <span className="text-primary font-bold leading-none">•</span>
                  <span>No fee unless we win your case</span>
                </li>
                <li className="flex items-center gap-3 text-[15px] md:text-[16px] text-slate-700">
                  <span className="text-primary font-bold leading-none">•</span>
                  <span>Licensed in North Carolina, South Carolina, and Tennessee</span>
                </li>
                <li className="flex items-center gap-3 text-[15px] md:text-[16px] text-slate-700">
                  <span className="text-primary font-bold leading-none">•</span>
                  <span>Focused personal injury representation</span>
                </li>
              </ul>

              {/* CTA Button */}
              <div>
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center bg-primary text-white font-semibold hover:bg-primary/90 transition-all duration-300 shadow-sm hover:shadow-md"
                  style={{ 
                    height: '48px', 
                    paddingLeft: '28px', 
                    paddingRight: '28px',
                    borderRadius: '12px',
                    fontSize: '15px'
                  }}
                  data-testid="free-consultation-cta"
                >
                  Free Consultation
                </Link>
              </div>
            </div>

            {/* Right Column - Image */}
            <div className="order-2 mt-6 md:mt-0 flex items-center">
              <div 
                className="w-full overflow-hidden"
                style={{ 
                  borderRadius: '6px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)'
                }}
              >
                <img
                  src="https://customer-assets.emergentagent.com/job_fc71d17f-2fef-4e00-b42c-dd0a285375c3/artifacts/v7eoepo7_unnamed.webp"
                  alt="Rizzi Law Group, PA"
                  className="w-full h-auto block"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Meet Your Attorneys */}
      <section className="py-12 md:py-[72px] bg-white" data-testid="meet-attorneys-section">
        <div className="w-full max-w-[1120px] mx-auto px-4 md:px-8">
          {/* Section Header */}
          <div className="max-w-2xl mb-8 md:mb-12">
            <h2 className="text-[24px] md:text-[34px] leading-[1.2] font-heading font-semibold text-slate-900 mb-4 tracking-tight">
              Meet Our Attorneys
            </h2>
            <p className="text-[16px] md:text-[18px] text-slate-700 leading-relaxed font-medium">
              Experienced personal injury attorneys serving North Carolina and South Carolina. When you hire Rizzi Law Group, you work directly with attorneys who prepare every case thoroughly and communicate clearly throughout the legal process.
            </p>
          </div>

          {/* 5 Attorney Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-6 mb-8 md:mb-10">
            {[
              { name: 'Mike Rizzi', title: 'Partner', img: 'https://customer-assets.emergentagent.com/job_fc71d17f-2fef-4e00-b42c-dd0a285375c3/artifacts/lv8xqmzz_Mike%20Headshot.jpg', testId: 'mike' },
              { name: 'Taylor Gordon', title: 'Partner', img: 'https://customer-assets.emergentagent.com/job_fc71d17f-2fef-4e00-b42c-dd0a285375c3/artifacts/050tut4g_Taylor%20Headshot%20%28updated%29.png', testId: 'taylor' },
              { name: 'Ashton Gordon', title: 'Partner', img: 'https://customer-assets.emergentagent.com/job_fc71d17f-2fef-4e00-b42c-dd0a285375c3/artifacts/ugvhsyfx_Ashton%20Headshot.jpg', testId: 'ashton' },
              { name: 'Doug Winn', title: 'Partner', img: 'https://customer-assets.emergentagent.com/job_fc71d17f-2fef-4e00-b42c-dd0a285375c3/artifacts/4z9lbxmp_Doug%20Headshot.jpg', testId: 'doug' },
              { name: 'Alex Sherret', title: 'Attorney', img: 'https://customer-assets.emergentagent.com/job_fc71d17f-2fef-4e00-b42c-dd0a285375c3/artifacts/3mfurf0g_Alex%20Headshot.jpg', testId: 'alex' },
            ].map((attorney) => (
              <Link
                key={attorney.testId}
                to="/our-team"
                className="group bg-white border border-slate-200 overflow-hidden hover:shadow-md transition-shadow"
                data-testid={`attorney-profile-${attorney.testId}`}
              >
                <div className="aspect-[4/5] overflow-hidden bg-slate-50">
                  <Headshot
                    name={attorney.testId}
                    alt={attorney.name}
                    sizes="(min-width:1024px) 210px, (min-width:640px) 30vw, 46vw"
                    className="w-full h-full object-cover object-top group-hover:scale-[1.02] transition-transform duration-300"
                  />
                </div>
                <div className="p-3 md:p-4 text-center">
                  <h3 className="text-[15px] md:text-[17px] font-heading font-semibold text-slate-900 leading-tight">
                    {attorney.name}
                  </h3>
                  <p className="text-[12px] md:text-[13px] text-primary font-medium uppercase tracking-wide mt-1">
                    {attorney.title}
                  </p>
                </div>
              </Link>
            ))}
          </div>

          <Link
            to="/our-team"
            className="inline-flex items-center justify-center bg-primary text-white font-semibold hover:bg-primary/90 transition-all duration-300"
            style={{ height: '48px', paddingLeft: '28px', paddingRight: '28px', borderRadius: '12px', fontSize: '15px' }}
            data-testid="meet-team-cta"
          >
            Meet Our Full Team
          </Link>
        </div>
      </section>

      {/* Proven Results for Injury Victims - Combined Case Results & Testimonials */}
      <section className="py-12 md:py-[88px] bg-slate-50" data-testid="results-testimonials-section">
        <div className="w-full max-w-[1120px] mx-auto px-4 md:px-8">
          {/* Section Title */}
          <div className="text-center mb-10 md:mb-16">
            <h2 className="text-[24px] md:text-[38px] leading-[1.2] font-heading font-semibold text-slate-900 mb-4 tracking-tight">
              Proven Results for Injury Victims
            </h2>
          </div>

          {/* Case Results Grid */}
          <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6 mb-8 md:mb-12">
            {/* Result 1 */}
            <div className="bg-white p-4 md:p-8 text-center" style={{ borderRadius: '8px', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)' }}>
              <div className="text-[22px] md:text-[36px] font-heading font-bold text-primary mb-1 md:mb-2">
                $2,050,000
              </div>
              <h3 className="text-[14px] md:text-[18px] font-heading font-semibold text-slate-900 mb-2 md:mb-3">
                Wrongful Death
              </h3>
              <p className="text-[12px] md:text-[14px] text-slate-600 leading-relaxed hidden sm:block">
                Vehicle struck by work van crossing the center line.
              </p>
            </div>

            {/* Result 2 */}
            <div className="bg-white p-4 md:p-8 text-center" style={{ borderRadius: '8px', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)' }}>
              <div className="text-[22px] md:text-[36px] font-heading font-bold text-primary mb-1 md:mb-2">
                $1,850,000
              </div>
              <h3 className="text-[14px] md:text-[18px] font-heading font-semibold text-slate-900 mb-2 md:mb-3">
                Car Accident
              </h3>
              <p className="text-[12px] md:text-[14px] text-slate-600 leading-relaxed hidden sm:block">
                Head-on collision causing severe injuries requiring multiple surgeries.
              </p>
            </div>

            {/* Result 3 */}
            <div className="bg-white p-4 md:p-8 text-center" style={{ borderRadius: '8px', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)' }}>
              <div className="text-[22px] md:text-[36px] font-heading font-bold text-primary mb-1 md:mb-2">
                $1,000,000
              </div>
              <h3 className="text-[14px] md:text-[18px] font-heading font-semibold text-slate-900 mb-2 md:mb-3">
                Workplace Accident
              </h3>
              <p className="text-[12px] md:text-[14px] text-slate-600 leading-relaxed hidden sm:block">
                Electrocution injury resulting in brain damage and memory deficits.
              </p>
            </div>

            {/* Result 4 */}
            <div className="bg-white p-4 md:p-8 text-center" style={{ borderRadius: '8px', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)' }}>
              <div className="text-[22px] md:text-[36px] font-heading font-bold text-primary mb-1 md:mb-2">
                $825,000
              </div>
              <h3 className="text-[14px] md:text-[18px] font-heading font-semibold text-slate-900 mb-2 md:mb-3">
                Wrongful Death
              </h3>
              <p className="text-[12px] md:text-[14px] text-slate-600 leading-relaxed hidden sm:block">
                Truck collision on mountain road resulting in fatal injuries.
              </p>
            </div>
          </div>

          {/* Disclaimer */}
          <p className="text-center text-[11px] md:text-[12px] text-slate-500 mb-10 md:mb-16 max-w-[800px] mx-auto">
            Past results do not guarantee future outcomes. Every case is different and must be evaluated individually.
          </p>

          {/* Google Reviews Header */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-2 mb-2">
              <svg viewBox="0 0 24 24" className="h-6 w-6" xmlns="http://www.w3.org/2000/svg">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              <span className="text-[32px] font-heading font-bold text-slate-900">4.9</span>
              <div className="flex gap-0.5">
                {[1,2,3,4,5].map((s) => (
                  <svg key={s} className="w-5 h-5 fill-amber-400 text-amber-400" viewBox="0 0 20 20">
                    <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                  </svg>
                ))}
              </div>
              <span className="text-sm text-slate-500">404 reviews</span>
            </div>
          </div>

          {/* Google Reviews Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-8 md:mb-12">
            {[
              { name: 'James R.', text: 'Rizzi Law Group handled my car accident case with incredible professionalism. They kept me updated every step of the way.' },
              { name: 'Michelle T.', text: 'Taylor and his team made the process so much easier. They truly care about their clients.' },
              { name: 'Brandon S.', text: 'Mike Rizzi and his team fought for me when the insurance company tried to lowball my claim. Got me what I deserved.' },
              { name: 'Patricia H.', text: 'From the first phone call, I felt like I was in good hands. The entire staff was kind, responsive, and professional.' },
            ].map((review, idx) => (
              <div key={idx} className="bg-white p-6" style={{ borderRadius: '8px', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)' }}>
                <div className="flex gap-0.5 mb-3">
                  {[1,2,3,4,5].map((s) => (
                    <svg key={s} className="w-4 h-4 fill-amber-400 text-amber-400" viewBox="0 0 20 20">
                      <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                    </svg>
                  ))}
                </div>
                <p className="text-[14px] text-slate-600 leading-relaxed mb-4">
                  "{review.text}"
                </p>
                <div className="flex items-center justify-between border-t border-slate-100 pt-3">
                  <span className="text-[13px] font-semibold text-slate-800">{review.name}</span>
                  <span className="text-[11px] text-slate-400">Google Review</span>
                </div>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="text-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center bg-primary text-white font-semibold hover:bg-primary/90 transition-all duration-300 shadow-sm hover:shadow-md"
              style={{ 
                height: '48px', 
                paddingLeft: '32px', 
                paddingRight: '32px',
                borderRadius: '12px',
                fontSize: '15px'
              }}
              data-testid="results-consultation-cta"
            >
              Free Consultation
            </Link>
            
            {/* Secondary Link */}
            <div className="mt-4">
              <Link
                to="/testimonials"
                className="text-primary font-medium hover:underline transition-colors"
                style={{ fontSize: '15px' }}
                data-testid="view-all-testimonials-link"
              >
                View All Testimonials & Results →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Practice Areas Section */}
      <section className="py-12 md:py-[88px] bg-white" data-testid="practice-areas-section">
        <div className="w-full max-w-[1120px] mx-auto px-4 md:px-8">
          <div className="text-center mb-10 md:mb-16">
            <h2 className="text-[24px] md:text-[34px] leading-[1.2] font-heading font-semibold text-slate-900 mb-4 tracking-tight">
              Practice Areas
            </h2>
            <p className="text-[15px] md:text-[17px] text-slate-600 max-w-[720px] mx-auto leading-relaxed">
              We focus exclusively on personal injury law, representing individuals and families harmed by negligence.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 mb-8 md:mb-12">
            {practiceAreas.map((area, index) => (
              <Link
                key={index}
                to={area.link}
                className="bg-white border border-slate-200 p-6 md:p-8 hover:shadow-xl transition-all duration-300 group rounded-xl"
                data-testid={`practice-area-card-${index}`}
              >
                <div className="text-primary mb-5 group-hover:scale-110 transition-transform duration-300">
                  {area.icon}
                </div>
                <h3 className="text-[20px] font-heading font-semibold text-slate-900 mb-3 leading-snug">
                  {area.title}
                </h3>
                <p className="text-slate-600 leading-relaxed text-[15px] mb-4">
                  {area.description}
                </p>
                <div className="flex items-center text-primary text-[14px] font-semibold group-hover:gap-2 transition-all">
                  <span>Learn more</span>
                  <ArrowRight className="h-4 w-4 ml-1 group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            ))}
          </div>

          <div className="text-center">
            <Link
              to="/practice-areas"
              className="w-full sm:w-auto inline-flex items-center justify-center border-2 border-primary text-primary px-8 sm:px-10 py-3.5 sm:py-4 font-semibold hover:bg-primary hover:text-white transition-all duration-300 text-[15px] rounded-xl"
              data-testid="view-all-practice-areas"
            >
              View All Practice Areas
            </Link>
          </div>
        </div>
      </section>

      {/* Service Area Callout */}
      <section className="py-12 md:py-[72px] bg-primary text-white" data-testid="service-area-section">
        <div className="w-full max-w-[1120px] mx-auto px-4 md:px-8 text-center">
          <h2 className="text-[22px] md:text-[30px] font-heading font-semibold mb-4 tracking-tight leading-snug">
            Serving North Carolina and South Carolina
          </h2>
          <p className="text-[15px] md:text-[17px] text-white/95 leading-relaxed max-w-[720px] mx-auto">
            We represent injured clients throughout North Carolina and South Carolina. Tennessee matters available where applicable.
          </p>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-12 md:py-[88px] bg-slate-50" data-testid="final-cta-section">
        <div className="w-full max-w-[1120px] mx-auto px-4 md:px-8 text-center">
          <h2 className="text-[24px] md:text-[34px] leading-[1.2] font-heading font-semibold text-slate-900 mb-4 md:mb-6 tracking-tight">
            Ready to Discuss Your Case?
          </h2>
          <p className="text-[15px] md:text-[17px] text-slate-600 mb-8 md:mb-12 leading-relaxed max-w-[720px] mx-auto">
            Contact us today for a free, no-obligation consultation.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center">
            <Link
              to="/contact"
              className="w-full sm:w-auto bg-primary text-white px-8 sm:px-10 py-3.5 sm:py-4 flex items-center justify-center font-semibold hover:bg-primary/90 transition-all duration-300 text-[15px] rounded-xl"
              data-testid="final-consultation-cta"
            >
              Request a Consultation
            </Link>
            <a
              href={phoneLink}
              className="w-full sm:w-auto border-2 border-primary text-primary px-8 sm:px-10 py-3.5 sm:py-4 flex items-center justify-center font-semibold hover:bg-primary hover:text-white transition-all duration-300 text-[15px] rounded-xl"
              data-testid="final-call-cta"
            >
              Call {phoneNumber}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
